import logger from '../lib/logger';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Plus, Search, MapPin, Building2, Eye, ChevronRight, X, Filter,
  FileText, Upload, Download, Phone, User, ChevronDown, ArrowLeft,
  CheckCircle, XCircle, Clock, Lock, TrendingUp, Trash2, RefreshCw, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { toast } from 'sonner';

const PLOT_STATUSES = [
  { value: 'all', label: 'All Status' },
  { value: 'available', label: 'Available' },
  { value: 'booked', label: 'Booked' },
  { value: 'sold', label: 'Sold' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'blocked', label: 'Blocked' },
];

const PLOT_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'sco', label: 'SCO' },
  { value: 'shop', label: 'Shop' },
  { value: 'booth', label: 'Booth' },
  { value: 'villa', label: 'Villa' },
  { value: 'plot', label: 'Plot' },
];

const PLOT_SIZES = [
  { value: 'all', label: 'All Sizes' },
  { value: '100', label: '100 sq.yds' },
  { value: '125', label: '125 sq.yds' },
  { value: '150', label: '150 sq.yds' },
  { value: '200', label: '200 sq.yds' },
  { value: '250', label: '250 sq.yds' },
  { value: '300', label: '300 sq.yds' },
  { value: '350', label: '350 sq.yds' },
  { value: '400', label: '400 sq.yds' },
  { value: '500', label: '500 sq.yds' },
  { value: '750', label: '750 sq.yds' },
  { value: '1000', label: '1000 sq.yds' },
];

const STATUS_COLORS = {
  available: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  booked: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  sold: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  negotiation: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  blocked: { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  hold: { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500' },
};

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetail, setProjectDetail] = useState(null);
  const [plots, setPlots] = useState([]);
  const [filteredPlots, setFilteredPlots] = useState([]);
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isPlotModalOpen, setIsPlotModalOpen] = useState(false);
  const [isPlotDetailOpen, setIsPlotDetailOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterBlock, setFilterBlock] = useState('all');
  const [filterSize, setFilterSize] = useState('all');
  const [selectedSizeCategory, setSelectedSizeCategory] = useState(null); // null = show size picker
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalResults, setGlobalResults] = useState([]);
  const [showGlobalResults, setShowGlobalResults] = useState(false);
  const [leads, setLeads] = useState([]);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Inventory sync
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const handleInventorySync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/inventory/sync');
      if (res.data.already_running) { toast.info('Sync already in progress'); }
      else { toast.success('Inventory sync started'); }
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const st = await api.get('/inventory/sync/status');
          const latest = st.data.logs?.[0];
          if (latest && latest.status !== 'running') {
            clearInterval(poll);
            setSyncing(false);
            setLastSync(latest);
            if (latest.status === 'success') {
              const p = latest.plots || {};
              toast.success(`Sync done: ${p.added || 0} added, ${p.updated || 0} updated`);
              fetchProjects();
            } else {
              toast.error(`Sync failed: ${latest.error || 'Unknown error'}`);
            }
          }
        } catch { /* ignore */ }
      }, 5000);
      // Safety: stop polling after 5 min
      setTimeout(() => { clearInterval(poll); setSyncing(false); }, 300000);
    } catch (e) {
      toast.error('Failed to start sync');
      setSyncing(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const res = await api.get('/inventory/sync/status');
      const latest = res.data.logs?.[0];
      if (latest) setLastSync(latest);
      if (latest?.status === 'running') setSyncing(true);
    } catch { /* ignore */ }
  };

  const [projectForm, setProjectForm] = useState({ name: '', location: '', description: '' });
  const [plotForm, setPlotForm] = useState({
    project_id: '', block: '', plot_number: '', size_sqyds: '', dimensions: '',
    plot_type: 'residential', price: '', price_per_sqyd: '', status: 'available',
    facing: '', floor: '', notes: '', owner_type: '', owner_name: '', owner_contact: '', condition: '',
  });

  useEffect(() => { fetchProjects(); fetchLeads(); fetchSyncStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedProject) {
      fetchProjectDetail(selectedProject.id);
      fetchPlots(selectedProject.id);
    }
  }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterPlotsData();
  }, [plots, searchTerm, filterStatus, filterType, filterBlock, filterSize, selectedSizeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProjects = async () => {
    try {
      const res = await api.get('/projects');
      setProjects(res.data);
    } catch (e) { toast.error('Failed to fetch projects'); }
  };

  const fetchLeads = async () => {
    try {
      const res = await api.get('/leads');
      setLeads(res.data);
    } catch (e) { logger.error('Failed to fetch leads'); }
  };

  const fetchProjectDetail = async (id) => {
    try {
      const res = await api.get(`/projects/${id}`);
      setProjectDetail(res.data);
    } catch (e) { logger.error('Failed to fetch project detail'); }
  };

  const fetchPlots = async (projectId) => {
    try {
      const res = await api.get(`/plots?project_id=${projectId}`);
      setPlots(res.data);
    } catch (e) { toast.error('Failed to fetch plots'); }
  };

  const filterPlotsData = useCallback(() => {
    let f = plots;
    // Size category filter
    if (selectedSizeCategory && selectedSizeCategory.size !== '__all') {
      if (selectedSizeCategory.size === '__other') {
        // "Other" = plots with sizes not in main categories — compute inline
        const mainSet = new Set();
        const catMap = {};
        for (const p of plots) {
          if (!p.size_sqyds) continue;
          const k = `${p.size_sqyds}|${p.size_unit || 'sq yd'}`;
          catMap[k] = (catMap[k] || 0) + 1;
        }
        for (const [k, count] of Object.entries(catMap)) {
          if (count >= 3) mainSet.add(k);
        }
        f = f.filter(p => !mainSet.has(`${p.size_sqyds}|${p.size_unit || 'sq yd'}`));
      } else {
        f = f.filter(p => p.size_sqyds === selectedSizeCategory.size && (p.size_unit || 'sq yd') === selectedSizeCategory.unit);
      }
    }
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      f = f.filter(p => p.plot_number?.toLowerCase().includes(t) || p.block?.toLowerCase().includes(t) || p.owner_name?.toLowerCase().includes(t));
    }
    if (filterStatus !== 'all') f = f.filter(p => p.status === filterStatus);
    if (filterType !== 'all') f = f.filter(p => p.plot_type === filterType);
    if (filterBlock !== 'all') f = f.filter(p => p.block === filterBlock);
    if (filterSize !== 'all') f = f.filter(p => p.size_sqyds === parseFloat(filterSize));
    setFilteredPlots(f);
  }, [plots, searchTerm, filterStatus, filterType, filterBlock, filterSize, selectedSizeCategory]);

  const handleGlobalSearch = async (q) => {
    setGlobalSearch(q);
    if (q.length < 2) { setGlobalResults([]); setShowGlobalResults(false); return; }
    try {
      const res = await api.get(`/plots/search?q=${encodeURIComponent(q)}`);
      setGlobalResults(res.data);
      setShowGlobalResults(true);
    } catch (e) { logger.error('Search failed'); }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await api.post('/projects', projectForm);
      toast.success('Project created!');
      setIsProjectModalOpen(false);
      setProjectForm({ name: '', location: '', description: '' });
      fetchProjects();
    } catch (e) { toast.error('Failed to create project'); }
  };

  const handleCreatePlot = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...plotForm,
        project_id: selectedProject.id,
        size_sqyds: plotForm.size_sqyds ? parseFloat(plotForm.size_sqyds) : null,
        price: plotForm.price ? parseFloat(plotForm.price) : null,
        price_per_sqyd: plotForm.price_per_sqyd ? parseFloat(plotForm.price_per_sqyd) : null,
      };
      await api.post('/plots', data);
      toast.success('Plot added!');
      setIsPlotModalOpen(false);
      setPlotForm({ project_id: '', block: '', plot_number: '', size_sqyds: '', dimensions: '', plot_type: 'residential', price: '', price_per_sqyd: '', status: 'available', facing: '', floor: '', notes: '', owner_type: '', owner_name: '', owner_contact: '', condition: '' });
      fetchPlots(selectedProject.id);
      fetchProjectDetail(selectedProject.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add plot'); }
  };

  const handleStatusUpdate = async (plotId, status) => {
    try {
      await api.put(`/plots/${plotId}/status?status=${status}`);
      toast.success('Status updated!');
      fetchPlots(selectedProject.id);
      fetchProjectDetail(selectedProject.id);
      if (selectedPlot?.id === plotId) setSelectedPlot({ ...selectedPlot, status });
    } catch (e) { toast.error('Failed to update status'); }
  };

  const handleTypeUpdate = async (plotId, plotType) => {
    try {
      await api.put(`/plots/${plotId}/type?plot_type=${plotType}`);
      toast.success('Type updated!');
      fetchPlots(selectedProject.id);
      if (selectedPlot?.id === plotId) setSelectedPlot({ ...selectedPlot, plot_type: plotType });
    } catch (e) { toast.error('Failed to update type'); }
  };

  const handleDeletePlot = async (plotId) => {
    if (!window.confirm('Are you sure you want to delete this plot?')) return;
    try {
      await api.delete(`/plots/${plotId}`);
      toast.success('Plot deleted!');
      fetchPlots(selectedProject.id);
      fetchProjectDetail(selectedProject.id);
      if (selectedPlot?.id === plotId) setIsPlotDetailOpen(false);
    } catch (e) { toast.error('Failed to delete plot'); }
  };

  const handleConditionUpdate = async (plotId, condition) => {
    try {
      await api.put(`/plots/${plotId}/condition?condition=${encodeURIComponent(condition)}`);
      toast.success('Condition updated!');
      fetchPlots(selectedProject.id);
      if (selectedPlot?.id === plotId) setSelectedPlot({ ...selectedPlot, condition });
    } catch (e) { toast.error('Failed to update condition'); }
  };

  const handlePlotPatch = async (plotId, field, value) => {
    try {
      await api.patch(`/plots/${plotId}`, { [field]: value });
      toast.success('Updated!');
      fetchPlots(selectedProject.id);
      if (selectedPlot?.id === plotId) setSelectedPlot(prev => ({ ...prev, [field]: value }));
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleAddLeadToPlot = async (plotId, leadId) => {
    try {
      await api.post(`/plots/${plotId}/leads?lead_id=${leadId}`);
      toast.success('Lead linked!');
      fetchPlots(selectedProject.id);
      const lead = leads.find(l => l.id === leadId);
      if (selectedPlot?.id === plotId) {
        const current = selectedPlot.linked_leads || [];
        setSelectedPlot({ ...selectedPlot, linked_leads: [...current, { id: leadId, name: lead?.name, phone: lead?.phone }] });
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to link lead'); }
  };

  const handleRemoveLeadFromPlot = async (plotId, leadId) => {
    try {
      await api.delete(`/plots/${plotId}/leads/${leadId}`);
      toast.success('Lead removed!');
      fetchPlots(selectedProject.id);
      if (selectedPlot?.id === plotId) {
        const current = selectedPlot.linked_leads || [];
        setSelectedPlot({ ...selectedPlot, linked_leads: current.filter(l => l.id !== leadId) });
      }
    } catch (e) { toast.error('Failed to remove lead'); }
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedProject) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/projects/${selectedProject.id}/import-plots`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const s = res.data.stats;
      toast.success(`Imported: ${s.imported}, Skipped: ${s.skipped}, Errors: ${s.errors}`);
      fetchPlots(selectedProject.id);
      fetchProjectDetail(selectedProject.id);
    } catch (e) { toast.error('CSV import failed'); }
    e.target.value = '';
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return '—';
    const p = typeof price === 'string' ? parseFloat(price.replace(/,/g, '')) : price;
    if (isNaN(p) || p === 0) return '—';
    if (p >= 10000000) return `₹${(p / 10000000).toFixed(2)} Cr`;
    if (p >= 100000) return `₹${(p / 100000).toFixed(1)} L`;
    return `₹${p.toLocaleString('en-IN')}`;
  };

  const getStatusBadge = (status) => {
    const c = STATUS_COLORS[status] || STATUS_COLORS.available;
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>{status}</span>;
  };

  const uniqueBlocks = useMemo(() => [...new Set(plots.map(p => p.block).filter(Boolean))], [plots]);

  // Compute size categories from actual plot data — canonical tab-derived sizes
  const sizeCategories = useMemo(() => {
    const map = {};
    for (const p of plots) {
      const sz = p.size_sqyds;
      const unit = p.size_unit || 'sq yd';
      if (!sz) continue;
      const key = `${sz}|${unit}`;
      if (!map[key]) map[key] = { size: sz, unit, count: 0, label: '' };
      map[key].count++;
    }
    const cats = Object.values(map);
    // Build labels
    for (const cat of cats) {
      if (cat.unit === 'acre') cat.label = `${cat.size} Acre`;
      else if (cat.unit === 'kanal') cat.label = `${cat.size} Kanal`;
      else cat.label = `${Number(cat.size).toLocaleString()} sq.yd.`;
    }
    // Separate main (count >= 3) and other
    const main = cats.filter(c => c.count >= 3).sort((a, b) => b.size - a.size);
    const other = cats.filter(c => c.count < 3);
    const otherTotal = other.reduce((s, c) => s + c.count, 0);
    return { main, otherTotal };
  }, [plots]);

  // Reset size category when project changes
  useEffect(() => { setSelectedSizeCategory(null); }, [selectedProject]);

  // ================ PROJECT LIST VIEW ================
  if (!selectedProject) {
    return (
      <Layout>
        <div className="aavaas-page">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
            <div>
              <h1 className="aavaas-page-title" data-testid="projects-title">Plots</h1>
              <p className="text-slate-600 mt-1">Manage plot projects and plot availability</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
              <div className="relative flex-1 lg:flex-initial">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search plots across all projects..." className="pl-10 w-full lg:w-[280px] bg-white" value={globalSearch} onChange={(e) => handleGlobalSearch(e.target.value)} data-testid="global-plot-search" />
                {showGlobalResults && globalResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border z-50 max-h-80 overflow-y-auto">
                    <div className="p-2 text-xs text-slate-500 border-b">{globalResults.length} plots found</div>
                    {globalResults.slice(0, 20).map(p => (
                      <div key={p.id} className="p-2 hover:bg-slate-50 cursor-pointer border-b border-slate-50 flex items-center justify-between" onClick={() => { setShowGlobalResults(false); setGlobalSearch(''); const proj = projects.find(pr => pr.id === p.project_id); if (proj) { setSelectedProject(proj); setTimeout(() => { setSelectedPlot(p); setIsPlotDetailOpen(true); }, 500); } }}>
                        <div>
                          <span className="font-medium text-sm">{p.project_name} - {p.block} {p.plot_number}</span>
                          <span className="text-xs text-slate-500 ml-2">{p.size_sqyds} sq.yds | {p.plot_type}</span>
                        </div>
                        {getStatusBadge(p.status)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {isAdmin && (
                <>
                <Button variant="outline" size="sm" onClick={handleInventorySync} disabled={syncing} data-testid="sync-plots-btn" className="gap-1.5">
                  {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {syncing ? 'Syncing...' : 'Sync Inventory'}
                </Button>
                <Dialog open={isProjectModalOpen} onOpenChange={setIsProjectModalOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-project-button"><Plus className="w-4 h-4 mr-2" /> Add Project</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Add New Project</DialogTitle></DialogHeader>
                    <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
                      <div><Label>Project Name *</Label><Input value={projectForm.name} onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })} required data-testid="project-name-input" /></div>
                      <div><Label>Location</Label><Input value={projectForm.location} onChange={(e) => setProjectForm({ ...projectForm, location: e.target.value })} placeholder="e.g., Mohali" /></div>
                      <div><Label>Description</Label><Textarea value={projectForm.description} onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })} rows={3} /></div>
                      <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-project">Create Project</Button>
                    </form>
                  </DialogContent>
                </Dialog>
                </>
              )}
            </div>
          </div>

          {/* Project Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map(project => (
              <Card key={project.id} className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedProject(project)} data-testid={`project-card-${project.id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">{project.name}</h3>
                      {project.location && <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{project.location}</p>}
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                  {project.description && <p className="text-sm text-slate-600 mb-4 line-clamp-2">{project.description}</p>}
                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    <div className="text-center p-2 bg-slate-50 rounded">
                      <p className="text-lg font-bold text-slate-900">{project.total_plots}</p>
                      <p className="text-[10px] text-slate-500">Total</p>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded">
                      <p className="text-lg font-bold text-green-600">{project.available_plots}</p>
                      <p className="text-[10px] text-green-600">Available</p>
                    </div>
                    <div className="text-center p-2 bg-blue-50 rounded">
                      <p className="text-lg font-bold text-blue-600">{project.booked_plots}</p>
                      <p className="text-[10px] text-blue-600">Booked</p>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded">
                      <p className="text-lg font-bold text-red-600">{project.sold_plots}</p>
                      <p className="text-[10px] text-red-600">Sold</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          {projects.length === 0 && (
            <div className="text-center py-16">
              <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-lg text-slate-500 font-medium">No projects yet</p>
              <p className="text-sm text-slate-400">Create your first project to start managing plots</p>
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // ================ PROJECT DETAIL VIEW ================
  // Size Picker Landing: show when no size selected and there are multiple sizes
  const showSizePicker = !selectedSizeCategory && sizeCategories.main.length > 1;

  return (
    <Layout>
      <div className="aavaas-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setSelectedProject(null); setPlots([]); setFilteredPlots([]); }} data-testid="back-to-projects">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="project-detail-title">
                {selectedProject.name}{selectedSizeCategory ? ` — ${selectedSizeCategory.label}` : ''}
              </h1>
              <p className="text-slate-500 text-sm">{selectedSizeCategory ? `${filteredPlots.length} plots` : (selectedProject.location || 'Plot Inventory')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <>
                <label className="cursor-pointer">
                  <input type="file" accept=".csv" className="hidden" onChange={handleCsvImport} />
                  <Button variant="outline" size="sm" asChild><span><Upload className="w-4 h-4 mr-1.5" /> Import CSV</span></Button>
                </label>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => setIsPlotModalOpen(true)} data-testid="add-plot-button">
                  <Plus className="w-4 h-4 mr-1.5" /> Add Plot
                </Button>
              </>
            )}
          </div>
        </div>

        {/* ===== SIZE PICKER LANDING ===== */}
        {showSizePicker && (
          <>
            <Card className="p-6 sm:p-8 mb-6 border-0 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-1">{selectedProject.name} Plot Inventory</h2>
              <p className="text-sm text-slate-500 mb-6">Choose plot size to view inventory</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {sizeCategories.main.map(cat => (
                  <button
                    key={`${cat.size}-${cat.unit}`}
                    className="group flex items-center justify-between px-4 py-3.5 bg-slate-50 hover:bg-slate-900 hover:text-white rounded-xl transition-all text-left"
                    onClick={() => setSelectedSizeCategory(cat)}
                    data-testid={`size-btn-${cat.size}`}
                  >
                    <span className="font-semibold text-sm text-slate-900 group-hover:text-white">{cat.label}</span>
                    <span className="text-xs text-slate-400 group-hover:text-slate-300 ml-2 tabular-nums">{cat.count}</span>
                  </button>
                ))}
                {sizeCategories.otherTotal > 0 && (
                  <button
                    className="group flex items-center justify-between px-4 py-3.5 bg-slate-50 hover:bg-slate-900 hover:text-white rounded-xl transition-all text-left"
                    onClick={() => setSelectedSizeCategory({ size: '__other', unit: 'other', label: 'Other', count: sizeCategories.otherTotal })}
                    data-testid="size-btn-other"
                  >
                    <span className="font-semibold text-sm text-slate-900 group-hover:text-white">Other</span>
                    <span className="text-xs text-slate-400 group-hover:text-slate-300 ml-2 tabular-nums">{sizeCategories.otherTotal}</span>
                  </button>
                )}
                <button
                  className="flex items-center justify-between px-4 py-3.5 border-2 border-dashed border-slate-200 hover:border-slate-400 rounded-xl transition-all text-left"
                  onClick={() => setSelectedSizeCategory({ size: '__all', unit: 'all', label: 'All Sizes', count: plots.length })}
                  data-testid="size-btn-all"
                >
                  <span className="font-semibold text-sm text-slate-500">View All</span>
                  <span className="text-xs text-slate-400 tabular-nums">{plots.length}</span>
                </button>
              </div>
            </Card>

            {/* Quick Stats */}
            {projectDetail && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-6">
                <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                  <p className="text-2xl font-bold text-slate-900">{projectDetail.total_plots}</p>
                  <p className="text-[11px] text-slate-500">Total</p>
                </div>
                <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                  <p className="text-2xl font-bold text-green-600">{projectDetail.available_plots}</p>
                  <p className="text-[11px] text-green-600">Available</p>
                </div>
                <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                  <p className="text-2xl font-bold text-blue-600">{projectDetail.booked_plots}</p>
                  <p className="text-[11px] text-blue-600">Booked</p>
                </div>
                <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                  <p className="text-2xl font-bold text-red-600">{projectDetail.sold_plots}</p>
                  <p className="text-[11px] text-red-600">Sold</p>
                </div>
                <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                  <p className="text-2xl font-bold text-amber-600">{projectDetail.negotiation_plots || 0}</p>
                  <p className="text-[11px] text-amber-600">Negotiation</p>
                </div>
              </div>
            )}

            {/* Other Projects */}
            {projects.filter(p => p.id !== selectedProject.id).length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Other Locations</h3>
                <div className="bg-white rounded-xl shadow-sm divide-y divide-slate-100 overflow-hidden">
                  {projects.filter(p => p.id !== selectedProject.id).map(p => (
                    <button key={p.id} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left" onClick={() => setSelectedProject(p)}>
                      <div>
                        <span className="font-medium text-sm text-slate-900">{p.name}</span>
                        {p.location && <span className="text-xs text-slate-400 ml-2">{p.location}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 tabular-nums">{p.total_plots || 0} plots</span>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ===== PLOT LIST VIEW (after size selected) ===== */}
        {!showSizePicker && (
          <>
            {/* Horizontal Size Tabs */}
            {sizeCategories.main.length > 1 && (
              <div className="mb-4 -mx-1 overflow-x-auto pb-1">
                <div className="flex gap-1.5 px-1 min-w-max">
                  <button
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${selectedSizeCategory?.size === '__all' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-400'}`}
                    onClick={() => setSelectedSizeCategory({ size: '__all', unit: 'all', label: 'All Sizes', count: plots.length })}
                  >All</button>
                  {sizeCategories.main.map(cat => (
                    <button
                      key={`${cat.size}-${cat.unit}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${selectedSizeCategory?.size === cat.size && selectedSizeCategory?.unit === cat.unit ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-400'}`}
                      onClick={() => setSelectedSizeCategory(cat)}
                    >
                      {cat.label} <span className="opacity-60">{cat.count}</span>
                    </button>
                  ))}
                  {sizeCategories.otherTotal > 0 && (
                    <button
                      className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${selectedSizeCategory?.size === '__other' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-400'}`}
                      onClick={() => setSelectedSizeCategory({ size: '__other', unit: 'other', label: 'Other', count: sizeCategories.otherTotal })}
                    >Other</button>
                  )}
                </div>
              </div>
            )}

            {/* Search + Filters */}
            <div className="bg-white rounded-xl shadow-sm mb-4">
              <div className="p-3 flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[160px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search plot no., owner, block..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-slate-50 border-0 h-9 text-sm" data-testid="plot-search" />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[110px] bg-slate-50 border-0 h-9 text-xs" data-testid="filter-plot-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{PLOT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-[110px] bg-slate-50 border-0 h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{PLOT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
                {uniqueBlocks.length > 0 && (
                  <Select value={filterBlock} onValueChange={setFilterBlock}>
                    <SelectTrigger className="w-[120px] bg-slate-50 border-0 h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Blocks</SelectItem>
                      {uniqueBlocks.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {(filterStatus !== 'all' || filterType !== 'all' || filterBlock !== 'all' || searchTerm) && (
                  <Button variant="ghost" size="sm" className="h-9 px-2" onClick={() => { setFilterStatus('all'); setFilterType('all'); setFilterBlock('all'); setSearchTerm(''); }}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
                <span className="text-xs text-slate-400 ml-auto tabular-nums">{filteredPlots.length} plots</span>
              </div>
            </div>

            {/* Plot Cards (mobile + desktop) */}
            <div className="space-y-2">
              {filteredPlots.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl shadow-sm">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium text-slate-500">No plots found</p>
                  <p className="text-sm text-slate-400">Adjust filters or add new plots</p>
                </div>
              ) : (
                filteredPlots.map(plot => {
                  const sc = STATUS_COLORS[plot.status] || STATUS_COLORS.available;
                  const sizeStr = plot.size_sqyds
                    ? `${Number(plot.size_sqyds).toLocaleString()} ${plot.size_unit === 'sq yd' || !plot.size_unit ? 'sq.yd.' : plot.size_unit}`
                    : (plot.size || '');
                  return (
                    <div key={plot.id} className="bg-white rounded-xl shadow-sm px-4 py-3 cursor-pointer hover:shadow-md transition-shadow flex items-center gap-3" onClick={() => { setSelectedPlot(plot); setIsPlotDetailOpen(true); }} data-testid={`plot-card-${plot.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-semibold text-sm text-slate-900">{plot.plot_number}</span>
                          {plot.block && <span className="text-xs text-slate-500">{plot.block}</span>}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                          {sizeStr && <span>{sizeStr}</span>}
                          {sizeStr && plot.plot_type && <span className="text-slate-300">·</span>}
                          {plot.plot_type && <span className="capitalize">{plot.plot_type}</span>}
                          {plot.facing && <><span className="text-slate-300">·</span><span>{plot.facing}</span></>}
                        </div>
                        {plot.price > 0 && <p className="text-xs font-medium text-slate-800 mt-0.5">{formatPrice(plot.price)}</p>}
                        {plot.owner_name && <p className="text-[11px] text-slate-400 mt-0.5 truncate">{plot.owner_name}{plot.owner_type && <span className={`ml-1 px-1 rounded text-[9px] font-medium ${plot.owner_type === 'owner' ? 'bg-emerald-50 text-emerald-600' : 'bg-violet-50 text-violet-600'}`}>{plot.owner_type}</span>}</p>}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${sc.bg} ${sc.text}`}>{plot.status}</span>
                        {isAdmin && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <Select value={plot.status} onValueChange={(v) => handleStatusUpdate(plot.id, v)}>
                              <SelectTrigger className="w-6 h-6 border-0 bg-transparent p-0"><ChevronDown className="w-3.5 h-3.5 text-slate-400" /></SelectTrigger>
                              <SelectContent>{PLOT_STATUSES.filter(s => s.value !== 'all').map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        )}
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* Plot Detail Sheet */}
        <Sheet open={isPlotDetailOpen} onOpenChange={setIsPlotDetailOpen}>
          <SheetContent className="w-full sm:max-w-md overflow-y-auto">
            {selectedPlot && (
              <>
                <SheetHeader className="mb-6">
                  <SheetTitle className="text-xl">Plot {selectedPlot.plot_number}</SheetTitle>
                  <div className="flex items-center gap-2 mt-1">
                    {getStatusBadge(selectedPlot.status)}
                    <span className="text-sm text-slate-500">{selectedPlot.project_name}</span>
                  </div>
                </SheetHeader>
                <div className="space-y-4">
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Plot Details <span className="text-xs text-slate-400 font-normal">(click to edit)</span></h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-slate-500">Project:</span><p className="font-medium">{selectedPlot.project_name}</p></div>
                      <div><span className="text-slate-500">Block:</span><p className="font-medium">{selectedPlot.block || '-'}</p></div>
                      <div><span className="text-slate-500">Plot No:</span><p className="font-medium">{selectedPlot.plot_number}</p></div>
                      <div><span className="text-slate-500">Size (sq.yds):</span>
                        <Input className="mt-1 h-7 text-sm" defaultValue={selectedPlot.size_sqyds || ''} placeholder="e.g., 500" type="number" onBlur={(e) => { const v = e.target.value; if (v !== String(selectedPlot.size_sqyds || '')) handlePlotPatch(selectedPlot.id, 'size_sqyds', v || null); }} /></div>
                      <div><span className="text-slate-500">Dimensions:</span>
                        <Input className="mt-1 h-7 text-sm" defaultValue={selectedPlot.dimensions || ''} placeholder="e.g., 30x75" onBlur={(e) => { const v = e.target.value.trim(); if (v !== (selectedPlot.dimensions || '')) handlePlotPatch(selectedPlot.id, 'dimensions', v || null); }} /></div>
                      <div><span className="text-slate-500">Type:</span><p className="font-medium capitalize">{selectedPlot.plot_type}</p></div>
                      <div><span className="text-slate-500">Price (₹):</span>
                        <Input className="mt-1 h-7 text-sm" defaultValue={selectedPlot.price || ''} placeholder="e.g., 65000000" type="number" onBlur={(e) => { const v = e.target.value; if (v !== String(selectedPlot.price || '')) handlePlotPatch(selectedPlot.id, 'price', v || null); }} /></div>
                      <div><span className="text-slate-500">Price/sq.yd:</span>
                        <Input className="mt-1 h-7 text-sm" defaultValue={selectedPlot.price_per_sqyd || ''} placeholder="e.g., 42000" type="number" onBlur={(e) => { const v = e.target.value; if (v !== String(selectedPlot.price_per_sqyd || '')) handlePlotPatch(selectedPlot.id, 'price_per_sqyd', v || null); }} /></div>
                      <div><span className="text-slate-500">Facing:</span>
                        <Input className="mt-1 h-7 text-sm" defaultValue={selectedPlot.facing || ''} placeholder="e.g., NE, Park" onBlur={(e) => { const v = e.target.value.trim(); if (v !== (selectedPlot.facing || '')) handlePlotPatch(selectedPlot.id, 'facing', v || null); }} /></div>
                      <div><span className="text-slate-500">Status:</span><p className="font-medium capitalize">{selectedPlot.status}</p></div>
                    </div>
                    <div className="mt-3 pt-3 border-t"><span className="text-slate-500 text-sm">Notes:</span>
                      <Textarea className="mt-1 text-sm" defaultValue={selectedPlot.notes || ''} placeholder="Add notes..." rows={2} onBlur={(e) => { const v = e.target.value.trim(); if (v !== (selectedPlot.notes || '')) handlePlotPatch(selectedPlot.id, 'notes', v || null); }} /></div>
                  </Card>
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Owner / Dealer <span className="text-xs text-slate-400 font-normal">(click to edit)</span></h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-slate-500">Type:</span>
                        <Select value={selectedPlot.owner_type || 'none'} onValueChange={(v) => handlePlotPatch(selectedPlot.id, 'owner_type', v === 'none' ? null : v)}>
                          <SelectTrigger className="mt-1 h-7 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="none">-</SelectItem><SelectItem value="owner">Owner</SelectItem><SelectItem value="dealer">Dealer</SelectItem></SelectContent>
                        </Select></div>
                      <div><span className="text-slate-500">Name:</span>
                        <Input className="mt-1 h-7 text-sm" defaultValue={selectedPlot.owner_name || ''} placeholder="Name" onBlur={(e) => { const v = e.target.value.trim(); if (v !== (selectedPlot.owner_name || '')) handlePlotPatch(selectedPlot.id, 'owner_name', v || null); }} /></div>
                      <div className="col-span-2"><span className="text-slate-500">Contact:</span>
                        <Input className="mt-1 h-7 text-sm" defaultValue={selectedPlot.owner_contact || ''} placeholder="Phone number" onBlur={(e) => { const v = e.target.value.trim(); if (v !== (selectedPlot.owner_contact || '')) handlePlotPatch(selectedPlot.id, 'owner_contact', v || null); }} /></div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-2">Condition / Agent Remarks</h3>
                    <Textarea defaultValue={selectedPlot.condition || ''} placeholder="e.g., GS 25%, Kothi built, ready to move..." rows={3} onBlur={(e) => { const val = e.target.value.trim(); if (val !== (selectedPlot.condition || '')) handleConditionUpdate(selectedPlot.id, val); }} data-testid="plot-condition-input" />
                  </Card>
                  {/* Linked Leads */}
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Linked Leads <span className="text-xs text-slate-400 font-normal">({(selectedPlot.linked_leads || []).length})</span></h3>
                    {(selectedPlot.linked_leads || []).length > 0 && (
                      <div className="space-y-2 mb-3">
                        {(selectedPlot.linked_leads || []).map(lead => (
                          <div key={lead.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-slate-800">{lead.name}</p>
                              {lead.phone && <a href={`tel:${lead.phone}`} className="text-xs text-blue-600">{lead.phone}</a>}
                            </div>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => handleRemoveLeadFromPlot(selectedPlot.id, lead.id)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <Select onValueChange={(v) => handleAddLeadToPlot(selectedPlot.id, v)}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="+ Add a lead..." /></SelectTrigger>
                      <SelectContent>
                        {leads.filter(l => !(selectedPlot.linked_leads || []).some(ll => ll.id === l.id)).slice(0, 50).map(l => (
                          <SelectItem key={l.id} value={l.id}>{l.name} - {l.phone}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Card>
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Update Type</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {['residential', 'commercial', 'sco', 'shop', 'booth', 'villa', 'plot'].map(t => (
                        <Button key={t} size="sm" variant={selectedPlot.plot_type === t ? 'default' : 'outline'} className="capitalize" onClick={() => handleTypeUpdate(selectedPlot.id, t)}>
                          {t}
                        </Button>
                      ))}
                    </div>
                  </Card>
                  {isAdmin && (
                    <Card className="p-4">
                      <h3 className="font-semibold text-slate-900 mb-3">Update Status</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {['available', 'booked', 'sold', 'negotiation'].map(s => (
                          <Button key={s} size="sm" variant={selectedPlot.status === s ? 'default' : 'outline'} className={`capitalize ${selectedPlot.status === s ? '' : ''}`} onClick={() => handleStatusUpdate(selectedPlot.id, s)}>
                            {s}
                          </Button>
                        ))}
                      </div>
                    </Card>
                  )}
                  {isAdmin && (
                    <Button variant="outline" className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => handleDeletePlot(selectedPlot.id)} data-testid="delete-plot-detail">
                      <Trash2 className="w-4 h-4 mr-2" /> Delete Plot
                    </Button>
                  )}
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>

        {/* Add Plot Modal */}
        <Dialog open={isPlotModalOpen} onOpenChange={setIsPlotModalOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Add Plot to {selectedProject.name}</DialogTitle></DialogHeader>
            <form onSubmit={handleCreatePlot} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Plot Number *</Label><Input value={plotForm.plot_number} onChange={(e) => setPlotForm({ ...plotForm, plot_number: e.target.value })} required data-testid="plot-number-input" /></div>
                <div><Label>Block</Label><Input value={plotForm.block} onChange={(e) => setPlotForm({ ...plotForm, block: e.target.value })} placeholder="e.g., Sector 66A" /></div>
                <div><Label>Size (sq.yds)</Label><Input type="number" value={plotForm.size_sqyds} onChange={(e) => setPlotForm({ ...plotForm, size_sqyds: e.target.value })} /></div>
                <div><Label>Dimensions</Label><Input value={plotForm.dimensions} onChange={(e) => setPlotForm({ ...plotForm, dimensions: e.target.value })} placeholder="e.g., 30x75" /></div>
                <div><Label>Type</Label>
                  <Select value={plotForm.plot_type} onValueChange={(v) => setPlotForm({ ...plotForm, plot_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PLOT_TYPES.filter(t => t.value !== 'all').map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Price (₹)</Label><Input type="number" value={plotForm.price} onChange={(e) => setPlotForm({ ...plotForm, price: e.target.value })} /></div>
                <div><Label>Facing</Label><Input value={plotForm.facing} onChange={(e) => setPlotForm({ ...plotForm, facing: e.target.value })} placeholder="e.g., North, Park Facing" /></div>
                <div><Label>Status</Label>
                  <Select value={plotForm.status} onValueChange={(v) => setPlotForm({ ...plotForm, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PLOT_STATUSES.filter(s => s.value !== 'all').map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Owner/Dealer Type</Label>
                  <Select value={plotForm.owner_type || 'none'} onValueChange={(v) => setPlotForm({ ...plotForm, owner_type: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="dealer">Dealer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Owner/Dealer Name</Label><Input value={plotForm.owner_name} onChange={(e) => setPlotForm({ ...plotForm, owner_name: e.target.value })} placeholder="e.g., Rajesh Kumar" /></div>
                <div><Label>Contact Number</Label><Input value={plotForm.owner_contact} onChange={(e) => setPlotForm({ ...plotForm, owner_contact: e.target.value })} placeholder="e.g., 9876543210" /></div>
              </div>
              <div><Label>Notes</Label><Textarea value={plotForm.notes} onChange={(e) => setPlotForm({ ...plotForm, notes: e.target.value })} rows={2} /></div>
              <div><Label>Condition / Remarks</Label><Textarea value={plotForm.condition} onChange={(e) => setPlotForm({ ...plotForm, condition: e.target.value })} rows={2} placeholder="e.g., GS 25%, Kothi built, ready to move..." /></div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-plot">Add Plot</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Projects;
